import { useCallback, useState } from 'react'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { useMountUnmount } from '@paytrace/pt-skel/Hooks'
import Event from '../Event'
import { TransactionHistorySearch as TransactionHistorySearchReq } from '@paytrace/pt-sdk'
import { parseTransactionSearchResponse } from '../Helper/ViewTransactions'
import Persistence from '@paytrace/pt-skel/Context/Persistence'
import {
  SearchStatus,
  TransactionSearchResponse,
} from '../types'

// This should allow some storage but if there is a whole bunch of transactions
// then must requery api and creating server side caching will be fine. Or we
// need to figure out something else. Just adding this caching just to help if
// it is a small query result.
const LIMIT_MB = 2 * 1024 * 1024 // 2MB
const PREFIX = 'view_tranx_results'

export type CacheKeyType = string | undefined

// storing transactions in storage to improve performance of opening in new tab
export async function storeTranxCache (searchResult: TransactionSearchResponse): Promise<CacheKeyType> {
  if (!searchResult?.transactions || !searchResult.summary) return undefined

  const cacheKey: CacheKeyType = `${PREFIX}-${Date.now()}`

  try {
    await Persistence.setBigItem(cacheKey, searchResult)
    return cacheKey
  } catch (err) {
    console.error('Failed to save transactions to storage:', err)
    return undefined
  }
}

// load transactions in storage to improve performance of opening in new tab
export async function loadTranxCache (
  cacheKey: CacheKeyType
): Promise<TransactionSearchResponse | null> {
  if (!cacheKey) return null

  try {
    const cachedData = await Persistence.getBigItem(cacheKey)
    if (!cachedData) return null

    if (!cachedData.transactions && !cachedData.summary) return null

    Persistence.clearBigItem(cacheKey)
    return cachedData
  } catch (err) {
    console.error('Failed to retrieve or parse results from sessionStorage:', err)
    return null
  }
}

// clear all transactions cache in storage for complete cleanup
export const clearTranxCache = () => {
  Persistence.clearBigItem(PREFIX, true)
}

// custom hook to reuse common methods and state for both view only (show all) and tranx search
export function useTransactionSearch (self: Symbol) {
  const [searchResponse, setSearchResponse] = useState<TransactionSearchResponse>()
  const [status, setStatus] = useState<SearchStatus>(SearchStatus.Initial)
  const [activeSearchCriteria, setActiveSearchCriteria] = useState<TransactionHistorySearchReq>()

  useMountUnmount(() => {
    PubSub.sub(self, Event.SEARCH_TRANSACTION_HISTORY_RESPONSE, (json) => {
      if (json.transactions) {
        setStatus(SearchStatus.Success)
        setSearchResponse(parseTransactionSearchResponse(json))
      } else {
        setStatus(SearchStatus.NotFound)
      }
    })

    return () => {
      PubSub.unsub(self)
    }
  })

  const doSearch = useCallback((searchCriteria: TransactionHistorySearchReq) => {
    setStatus(SearchStatus.Searching)

    PubSub.pub('VALIDATION_ERROR::clear')
    PubSub.pub(Event.SEARCH_TRANSACTION_HISTORY_REQUEST, searchCriteria)
  }, [])

  return {
    doSearch,
    searchResponse,
    setSearchResponse,
    status,
    setStatus,
    activeSearchCriteria,
    setActiveSearchCriteria,
  }
}
